import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { NavigationTabs } from "@/components/common/navigationTabs";
import { TableContainer } from "@/components/common/TableContainer";
import { DataView } from "@/components/common/DataView";
import { ActionButton } from "@/components/common/DataViewTypes";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useSelector } from "react-redux";
import { RootState } from "@/store/store";
import { useAppDispatch } from "@/store/hooks";
import { FileText, Loader2, Plus, Upload, RefreshCw, X } from "lucide-react";
import useProducts from "@/hooks/useProducts";
import { useConfirmation } from "@/components/common/ConfirmationDialog";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { getColumns } from "./columns";
import { Product } from "@/types/models/product";
import { searchProductsRequest, clearProductSearchResults, fetchProductsRequest } from "@/store/slices/productNew.slice";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { importDummyProducts, importProductsFromFile } from "@/services/productService";

const productTabs = [
  { label: "Active", status: "ACTIVE" },
  { label: "Draft", status: "DRAFT" },
  { label: "Inactive", status: "INACTIVE" },
  { label: "All", status: null },
];

const DEFAULT_DUMMY_PRODUCT_COUNT = 5;
const MAX_DUMMY_PRODUCT_COUNT = 100;

export default function Products() {
  const params = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { confirm } = useConfirmation();
  const dispatch = useAppDispatch();

  // Get both listing and search state from Redux
  const {
    items,
    loading,
    error,
    pagination,
    fetchItems,
  } = useProducts();

  const searchResults = useSelector((state: RootState) => state.products.searchResults);
  const searchLoading = useSelector((state: RootState) => state.products.searchLoading);
  const searchError = useSelector((state: RootState) => state.products.searchError);
  const searchPagination = useSelector((state: RootState) => state.products.searchPagination);

  const initialStatus = searchParams.get("status");
  const initialCollection = searchParams.get("collection") || "";
  const initialPage = Number(searchParams.get("page") || "1");
  const [pageIndex, setPageIndex] = useState<number>(Number.isFinite(initialPage) && initialPage > 0 ? initialPage - 1 : 0);
  const [activeTab, setActiveTab] = useState<string | null>(initialStatus ? initialStatus : (initialCollection ? null : "ACTIVE"));
  const [searchQuery, setSearchQuery] = useState<string>(searchParams.get("q") || "");
  const [collectionFilter, setCollectionFilter] = useState<string>(initialCollection);
  const [sortBy, setSortBy] = useState<string>(searchParams.get("sortBy") || "updated_at");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">((searchParams.get("sortOrder") as "asc" | "desc") || "desc");
  const [isImportingDummyData, setIsImportingDummyData] = useState(false);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [selectedImportFile, setSelectedImportFile] = useState<File | null>(null);
  const [dummyProductCount, setDummyProductCount] = useState(DEFAULT_DUMMY_PRODUCT_COUNT);
  const [isImportingProducts, setIsImportingProducts] = useState(false);
  const pageSize = 10;
  const lastRequestKeyRef = useRef<string>("");
  const importFileInputRef = useRef<HTMLInputElement | null>(null);

  // Create columns with current store ID
  const columns = useMemo(() => {
    return getColumns();
  }, []);

  const fetchProducts = useCallback(() => {
    const hasSearch = searchQuery.trim();
    const filters: Record<string, any> = {};
    if (activeTab) filters.status = activeTab;
    if (collectionFilter) filters.collection_id = collectionFilter;

    if (hasSearch) {
      // Use search API when there's a search query
      const searchParams: any = {
        query: searchQuery,
        page: pageIndex + 1,
        pageSize: pageSize,
        sortBy,
        sortOrder,
        filters,
      };

      dispatch(searchProductsRequest(searchParams));
    } else {
      // Clear search results when switching back to listing
      dispatch(clearProductSearchResults());

      // Use listing API when there's no search query
      const requestParams: any = {
        page: pageIndex + 1,
        pageSize: pageSize,
        sortBy,
        sortOrder,
        filters,
      };

      fetchItems(requestParams);
    }
  }, [searchQuery, pageIndex, activeTab, collectionFilter, sortBy, sortOrder, fetchItems, dispatch]);

  // Use search state when searching, otherwise use listing state
  const hasSearch = searchQuery.trim();
  const currentData = hasSearch ? (searchResults || []) : (items || []);
  const currentPagination = hasSearch
    ? {
        totalPages: searchPagination?.totalPages || 1,
        currentPage: searchPagination?.page || 1,
        totalItems: searchPagination?.total || 0,
        pageSize: searchPagination?.pageSize || 10,
        total: searchPagination?.total || 0,
      }
    : {
        totalPages: pagination?.totalPages || 1,
        currentPage: (pagination?.page || 0) + 1,
        totalItems: pagination?.total || 0,
        pageSize: pagination?.pageSize || 10,
        total: pagination?.total || 0,
      };
  const currentLoading = hasSearch ? searchLoading : loading;
  const currentError = hasSearch ? searchError : error;

  // Keep URL in sync so routing into this page can preset filters/sort/page
  useEffect(() => {
    const next = new URLSearchParams();
    if (activeTab) next.set("status", activeTab);
    if (searchQuery.trim()) next.set("q", searchQuery.trim());
    if (collectionFilter) next.set("collection", collectionFilter);
    if (sortBy) next.set("sortBy", sortBy);
    if (sortOrder) next.set("sortOrder", sortOrder);
    if (pageIndex > 0) next.set("page", String(pageIndex + 1));
    setSearchParams(next, { replace: true });
  }, [activeTab, searchQuery, collectionFilter, sortBy, sortOrder, pageIndex, setSearchParams]);

  // If URL is changed externally (e.g. from collections page), hydrate UI state from it.
  useEffect(() => {
    const urlStatus = searchParams.get("status");
    const urlQuery = searchParams.get("q") || "";
    const urlCollection = searchParams.get("collection") || "";
    const urlSortBy = searchParams.get("sortBy") || "updated_at";
    const urlSortOrder = (searchParams.get("sortOrder") as "asc" | "desc") || "desc";
    const urlPage = Number(searchParams.get("page") || "1");

    setActiveTab(urlStatus || null);
    setSearchQuery(urlQuery);
    setCollectionFilter(urlCollection);
    setSortBy(urlSortBy);
    setSortOrder(urlSortOrder);
    setPageIndex(Number.isFinite(urlPage) && urlPage > 0 ? urlPage - 1 : 0);
  }, [searchParams]);

  useEffect(() => {
    const requestKey = JSON.stringify({
      mode: searchQuery.trim() ? "search" : "list",
      page: pageIndex,
      status: activeTab,
      q: searchQuery.trim(),
      collection: collectionFilter,
      sortBy,
      sortOrder,
    });
    const shouldFetch = currentData.length === 0 || lastRequestKeyRef.current !== requestKey;
    if (shouldFetch) {
      fetchProducts();
      lastRequestKeyRef.current = requestKey;
    }
  }, [pageIndex, activeTab, searchQuery, collectionFilter, sortBy, sortOrder, currentData.length, fetchProducts]);

  const handleSearch = (query: string) => {
    setSearchQuery(query);
    setPageIndex(0);
  };

  const handleTabChange = (status: string | null) => {
    setActiveTab(status);
    setPageIndex(0);
  };

  const clearCollectionFilter = () => {
    setCollectionFilter("");
    setPageIndex(0);
  };

  const clearStatusFilter = () => {
    setActiveTab(null);
    setPageIndex(0);
  };

  const handleRowClick = (product: Product) => {
    if (!params) return;
    const storeType = params.storeType as string;
    const storeId = params.storeId as string;
    navigate(`/${storeType}/${storeId}/product/create?q=${product.id}`);
  };

  const handlePageChange = (newPageIndex: number) => setPageIndex(newPageIndex);

  const getStatusTitle = () => {
    if (!activeTab) return "All Products";
    switch (activeTab) {
      case "ACTIVE":
        return "Active Products";
      case "DRAFT":
        return "Draft Products";
      case "INACTIVE":
        return "Inactive Products";
      default:
        return "Products";
    }
  };

  const getStatusDescription = () => {
    if (!activeTab) return "Manage your product catalog, inventory, and pricing";
    switch (activeTab) {
      case "ACTIVE":
        return "Products currently available for sale";
      case "DRAFT":
        return "Products in development or awaiting approval";
      case "INACTIVE":
        return "Products temporarily unavailable";
      default:
        return "Manage your product catalog";
    }
  };

  const handleAddProduct = () => {
    if (!params) return;
    const storeType = params.storeType as string;
    const storeId = params.storeId as string;
    navigate(`/${storeType}/${storeId}/product/create`);
  };

  const openImportModal = () => {
    setSelectedImportFile(null);
    setDummyProductCount(DEFAULT_DUMMY_PRODUCT_COUNT);
    if (importFileInputRef.current) {
      importFileInputRef.current.value = "";
    }
    setIsImportModalOpen(true);
  };

  const closeImportModal = () => {
    setIsImportModalOpen(false);
    setSelectedImportFile(null);
    if (importFileInputRef.current) {
      importFileInputRef.current.value = "";
    }
  };

  const handleImportFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] || null;
    if (!file) {
      setSelectedImportFile(null);
      return;
    }

    const fileName = file.name.toLowerCase();
    const isSupportedFile =
      fileName.endsWith(".csv") || fileName.endsWith(".json");

    if (!isSupportedFile) {
      toast.error("Please select a CSV or JSON file");
      event.target.value = "";
      setSelectedImportFile(null);
      return;
    }

    setSelectedImportFile(file);
  };

  const handleImportProducts = async () => {
    if (!selectedImportFile || isImportingProducts) {
      if (!selectedImportFile) {
        toast.error("Please choose a CSV or JSON file first");
      }
      return;
    }

    try {
      setIsImportingProducts(true);
      await importProductsFromFile(selectedImportFile);
      toast.success("Products imported successfully");
      closeImportModal();
      handleRefresh();
    } catch (err: any) {
      toast.error(err?.message || "Failed to import products");
    } finally {
      setIsImportingProducts(false);
    }
  };

  const handleAddDummyData = async () => {
    if (isImportingDummyData) return;

    try {
      setIsImportingDummyData(true);
      await importDummyProducts(dummyProductCount);
      toast.success(`${dummyProductCount} dummy products imported successfully`);
      closeImportModal();
      handleRefresh();
    } catch (err: any) {
      toast.error(err?.message || "Failed to import dummy products");
    } finally {
      setIsImportingDummyData(false);
    }
  };

  // Handle refresh - directly dispatch action to force a fresh fetch
  const handleRefresh = () => {
    const hasSearchQuery = searchQuery.trim();
    const filters: Record<string, any> = {};
    if (activeTab) filters.status = activeTab;
    if (collectionFilter) filters.collection_id = collectionFilter;

    if (hasSearchQuery) {
      const searchParams: any = {
        query: searchQuery,
        page: pageIndex + 1,
        pageSize: pageSize,
        sortBy,
        sortOrder,
        filters,
      };
      dispatch(searchProductsRequest(searchParams));
    } else {
      dispatch(clearProductSearchResults());
      const requestParams: any = {
        page: pageIndex + 1,
        pageSize: pageSize,
        sortBy,
        sortOrder,
        filters,
      };
      dispatch(fetchProductsRequest(requestParams));
    }
  };

  const activeFilterChips = [
    collectionFilter
      ? {
          key: "collection",
          label: `Collection: ${collectionFilter}`,
          onRemove: clearCollectionFilter,
        }
      : null,
    activeTab
      ? {
          key: "status",
          label: `Status: ${activeTab}`,
          onRemove: clearStatusFilter,
        }
      : null,
  ].filter(Boolean) as Array<{ key: string; label: string; onRemove: () => void }>;

  const actionButtons: ActionButton[] = [
    {
      label: "Refresh",
      icon: <RefreshCw className={`h-3 w-3 ${currentLoading ? 'animate-spin' : ''}`} />,
      onClick: handleRefresh,
      variant: "outline",
      disabled: currentLoading,
    },
    {
      label: "Add Product",
      icon: <Plus className="h-3 w-3" />,
      onClick: handleAddProduct,
      variant: "primary",
    },
    {
      label: "Import",
      icon: <Upload className="h-3 w-3" />,
      onClick: openImportModal,
      variant: "outline",
    },
  ];

  return (
    <div className="space-y-4">
      <Dialog
        open={isImportModalOpen}
        onOpenChange={(open) => {
          if (open) {
            openImportModal();
          } else {
            closeImportModal();
          }
        }}
      >
        <DialogContent className="sm:max-w-[560px] bg-card border border-border/40 shadow-2xl">
          <DialogHeader className="pb-4 border-b border-border/60">
            <DialogTitle className="flex items-center gap-2 text-lg font-semibold text-foreground">
              <Upload className="h-5 w-5" />
              Import Products
            </DialogTitle>
            <DialogDescription className="text-sm text-muted-foreground">
              Import products from a CSV or JSON file. Use fake data if you just want to populate the catalog quickly.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground">
                Product file
              </label>
              <div className="rounded-xl border border-dashed border-border/70 bg-muted/30 p-4">
                <input
                  ref={importFileInputRef}
                  type="file"
                  accept=".csv,.json,text/csv,application/json"
                  onChange={handleImportFileChange}
                  className="block w-full text-sm text-muted-foreground file:mr-4 file:rounded-full file:border-0 file:bg-primary file:px-4 file:py-2 file:text-sm file:font-medium file:text-primary-foreground hover:file:bg-primary/90"
                />
                <p className="mt-2 text-xs text-muted-foreground">
                  Supported formats: CSV and JSON.
                </p>
                {selectedImportFile && (
                  <div className="mt-3 flex items-center gap-2 rounded-lg bg-background px-3 py-2 text-sm text-foreground">
                    <FileText className="h-4 w-4 text-muted-foreground" />
                    <span className="truncate">{selectedImportFile.name}</span>
                  </div>
                )}
              </div>
            </div>

            <div className="rounded-xl border bg-secondary-background p-4">
              <div className="flex items-start gap-3">
                <div className="flex h-9 w-9 items-center justify-center rounded-full bg-primary/10 text-primary">
                  <Plus className="h-4 w-4" />
                </div>
                <div className="flex-1">
                  <h3 className="text-sm font-semibold text-foreground">
                    Need sample products?
                  </h3>
                  <p className="mt-1 text-xs text-muted-foreground">
                    Add fake data to quickly populate the product table for testing or demos.
                  </p>
                  <div className="mt-4 space-y-2">
                    <div className="flex items-center justify-between gap-3">
                      <label htmlFor="dummy-product-count" className="text-xs font-medium text-foreground">
                        Number of dummy products
                      </label>
                      <span className="rounded-full bg-background px-2.5 py-1 text-xs font-semibold text-foreground">
                        {dummyProductCount}
                      </span>
                    </div>
                    <input
                      id="dummy-product-count"
                      type="range"
                      min={1}
                      max={MAX_DUMMY_PRODUCT_COUNT}
                      value={dummyProductCount}
                      onChange={(event) => setDummyProductCount(Number(event.target.value))}
                      disabled={isImportingDummyData || isImportingProducts}
                      className="h-2 w-full cursor-pointer appearance-none rounded-full bg-border accent-primary disabled:cursor-not-allowed disabled:opacity-60"
                    />
                    <div className="flex justify-between text-[11px] text-muted-foreground">
                      <span>1</span>
                      <span>{MAX_DUMMY_PRODUCT_COUNT}</span>
                    </div>
                  </div>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleAddDummyData}
                  disabled={isImportingDummyData || isImportingProducts}
                >
                  {isImportingDummyData ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Plus className="h-4 w-4" />
                  )}
                  Add {dummyProductCount} fake products
                </Button>
              </div>
            </div>
          </div>

          <DialogFooter className="flex flex-col gap-2 border-t border-border/60 pt-4 sm:flex-row sm:justify-end">
            <Button type="button" variant="outline" onClick={closeImportModal}>
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleImportProducts}
              disabled={!selectedImportFile || isImportingProducts || isImportingDummyData}
            >
              {isImportingProducts ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Upload className="h-4 w-4" />
              )}
              Import file
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <TableContainer
        navigationTabs={
          <NavigationTabs
            tabs={productTabs.map((tab) => ({
              label: tab.label,
              onClick: () => handleTabChange(tab.status),
              isActive: activeTab === tab.status,
            }))}
            hasBackButton={false}
          />
        }
      >
        <div className="bg-secondary-background p-[18px] border rounded-[16px]">
          <div className="flex flex-col gap-3 mb-3">
            <div className="flex flex-wrap items-center gap-2">
              {activeFilterChips.map((chip) => (
                <Badge key={chip.key} variant="secondary" className="rounded-full pl-3 pr-2 py-1 text-xs gap-1">
                  {chip.label}
                  <button
                    type="button"
                    onClick={chip.onRemove}
                    className="inline-flex items-center justify-center rounded-full hover:bg-black/10 p-0.5"
                    aria-label={`Remove ${chip.label} filter`}
                  >
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              ))}
            </div>
            <div className="flex items-center gap-2">
              <select
                value={sortBy}
                onChange={(e) => {
                  setSortBy(e.target.value);
                  setPageIndex(0);
                }}
                className="h-8 text-xs rounded-full border px-3 bg-background"
              >
                <option value="updated_at">Sort by Updated</option>
                <option value="created_at">Sort by Created</option>
                <option value="title">Sort by Title</option>
                <option value="price">Sort by Price</option>
              </select>
              <select
                value={sortOrder}
                onChange={(e) => {
                  setSortOrder(e.target.value as "asc" | "desc");
                  setPageIndex(0);
                }}
                className="h-8 text-xs rounded-full border px-3 bg-background"
              >
                <option value="desc">Descending</option>
                <option value="asc">Ascending</option>
              </select>
            </div>
          </div>
          <DataView
            columns={columns}
            data={currentData}
            pagination={currentPagination}
            pageIndex={pageIndex}
            onPageChange={handlePageChange}
            loading={currentLoading || false}
            error={currentError?.message || null}
            title={getStatusTitle()}
            description={getStatusDescription()}
            searchPlaceholder="Search products..."
            searchQuery={searchQuery}
            onSearch={handleSearch}
            actionButtons={actionButtons}
            onRowClick={handleRowClick}
            enableColumnResizing={true}
            enableRowSelection={false}
            emptyStateType="products"
            loadingMessage="Loading..."
          />
        </div>
      </TableContainer>
    </div>
  );
}
